import React, { useEffect, useState } from "react";
import axios from "axios";
import Layout, { DateInput } from "./Layout";
import { formatDate, getApiUrl, getTodayIso } from "./config";

function CustomerBills() {
    const today = getTodayIso();
    const [bills, setBills] = useState([]);
    const [viewInvoice, setViewInvoice] = useState(null);
    const [from, setFrom] = useState("");
    const [to, setTo] = useState("");
    const [customerLabel, setCustomerLabel] = useState("-");

    const formatPaymentSplit = (cash, upi, bank) => {
        const c = Number(cash) || 0;
        const u = Number(upi) || 0;
        const b = Number(bank) || 0;
        const parts = [];
        if (c > 0) parts.push(`Cash ₹ ${c.toFixed(2)}`);
        if (u > 0) parts.push(`UPI ₹ ${u.toFixed(2)}`);
        if (b > 0) parts.push(`Bank ₹ ${b.toFixed(2)}`);
        return parts.length > 0 ? parts.join(" | ") : "-";
    };

    useEffect(() => {
        const token = localStorage.getItem("token");
        if (!token) return;

        const fetchBills = async () => {
            try {
                const params = {};
                if (from) params.from = from;
                if (to) params.to = to;
                const res = await axios.get(`${getApiUrl()}/customer/sales`, {
                    headers: { Authorization: `Bearer ${token}` },
                    params,
                });
                setBills(res.data || []);
            } catch (err) {
                console.error(err);
            }
        };
        fetchBills();
    }, [from, to]);

    useEffect(() => {
        const customerId = localStorage.getItem("customer_id");
        if (!customerId) return;
        axios
            .get(`${getApiUrl()}/customers`)
            .then((res) => {
                const match = (res.data || []).find((c) => String(c.id) === String(customerId));
                if (match && match.name) setCustomerLabel(match.name);
            })
            .catch(() => null);
    }, []);

    const handleView = async (id) => {
        const token = localStorage.getItem("token");
        if (!token) return;
        try {
            const res = await axios.get(`${getApiUrl()}/customer/sales/${id}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setViewInvoice(res.data);
        } catch (err) {
            console.error(err);
        }
    };

    return (
        <Layout>
            <div className="container mt-4">
                <h2>Service Invoice Report</h2>
                <div className="card p-4 mb-3">
                    <div className="row g-3 align-items-end">
                        <div className="col-md-3">
                            <label className="form-label fw-bold">From</label>
                            <DateInput value={from} onChange={setFrom} />
                        </div>
                        <div className="col-md-3">
                            <label className="form-label fw-bold">To</label>
                            <DateInput value={to} onChange={setTo} />
                        </div>
                        <div className="col-md-3 d-grid">
                            <button type="button" className="btn btn-outline-secondary" onClick={() => { setFrom(today); setTo(today); }}>
                                Today
                            </button>
                        </div>
                        <div className="col-md-3 d-grid">
                            <button type="button" className="btn btn-outline-secondary" onClick={() => { setFrom(""); setTo(""); }}>
                                Clear
                            </button>
                        </div>
                    </div>
                </div>

                <div className="table-responsive">
                    <table className="table table-striped">
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Customer</th>
                                <th>Vehicle No</th>
                                <th>Model</th>
                                <th>Job Card</th>
                                <th>Items</th>
                                <th>Qty</th>
                                <th>Invoice Total</th>
                                <th>Paid Amount</th>
                                <th>Paid Split</th>
                                <th>Receipt</th>
                                <th>Receipt Split</th>
                                <th>Net Bal</th>
                            </tr>
                        </thead>
                        <tbody>
                            {bills.map((b) => (
                                <tr key={b.invoice_id} style={{ cursor: "pointer" }} onClick={() => handleView(b.invoice_id)}>
                                    <td>{formatDate(b.date)}</td>
                                    <td>{customerLabel}</td>
                                    <td>{b.vehicle_no || "-"}</td>
                                    <td>{b.service_vehicle_model || "-"}</td>
                                    <td>{b.job_card_no || "-"}</td>
                                    <td>{b.items_summary || "-"}</td>
                                    <td>{Number(b.total_qty || 0)}</td>
                                    <td className="fw-bold">₹ {(Number(b.total_amount) || 0).toFixed(2)}</td>
                                    <td className="text-success">₹ {(Number(b.paid_amount) || 0).toFixed(2)}</td>
                                    <td>{formatPaymentSplit(b.cash_amount, b.upi_amount, b.bank_amount)}</td>
                                    <td className="text-success">₹ {(Number(b.receipt_amount) || 0).toFixed(2)}</td>
                                    <td>{formatPaymentSplit(b.receipt_cash_amount, b.receipt_upi_amount, b.receipt_bank_amount)}</td>
                                    <td className="text-danger fw-bold">₹ {(Number(b.cumulative_balance ?? b.net_balance) || 0).toFixed(2)}</td>
                                </tr>
                            ))}
                            {bills.length === 0 && (
                                <tr>
                                    <td colSpan="13" className="text-center">
                                        No bills found
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {viewInvoice && (
                    <div className="modal show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
                        <div className="modal-dialog modal-lg">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">Service Invoice #{viewInvoice.id}</h5>
                                    <button type="button" className="btn-close" onClick={() => setViewInvoice(null)}></button>
                                </div>
                                <div className="modal-body">
                                    <p><strong>Job Card No:</strong> {viewInvoice.job_card_no || "-"}</p>
                                    <p><strong>Date:</strong> {formatDate(viewInvoice.date)}</p>
                                    <p><strong>Customer:</strong> {viewInvoice.customer_name || customerLabel || "-"}</p>
                                    <p><strong>Customer Mobile:</strong> {viewInvoice.customer_contact || "-"}</p>
                                    <p><strong>Vehicle No:</strong> {viewInvoice.vehicle_no || "-"}</p>
                                    <p><strong>Model:</strong> {viewInvoice.service_vehicle_model || "-"}</p>
                                    <p><strong>Km Reading:</strong> {viewInvoice.service_km || "-"}</p>
                                    <p><strong>Paid:</strong> ₹ {(Number(viewInvoice.paid_amount) || 0).toFixed(2)} ({formatPaymentSplit(viewInvoice.cash_amount, viewInvoice.upi_amount, viewInvoice.bank_amount)})</p>
                                    <table className="table table-bordered">
                                        <thead>
                                            <tr>
                                                <th>Item / Service</th>
                                                <th>Quantity</th>
                                                <th>Rate</th>
                                                <th>Total</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(viewInvoice.items || []).map((item, idx) => (
                                                <tr key={idx}>
                                                    <td>{item.product_name}</td>
                                                    <td>{Number(item.quantity || 0)}</td>
                                                    <td>{Number(item.price || 0).toFixed(2)}</td>
                                                    <td>{Number(item.total || 0).toFixed(2)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                        <tfoot>
                                            <tr>
                                                <th colSpan="3" className="text-end">Grand Total</th>
                                                <th>₹ {(Number(viewInvoice.total_amount) || 0).toFixed(2)}</th>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={() => setViewInvoice(null)}>
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
}

export default CustomerBills;
